import { DotLegalDialog, DotLegalFullScreenSpinner, DotLegalHeader, DotLegalLink } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import { useLegalEntityTypeUsageDialog } from "./LegalEntityTypeUsageDialog.hooks.ts";
import { useTranslation } from "@localization/useTranslation.ts";
import { useUrlProvider } from "../../useUrlProvider.ts";
import { StandardLegalEntityType } from "../LegalEntity.types.ts";
import { useLegalEntityTypeNames } from "../useLegalEntityTypeNames.ts";

export interface LegalEntityUsageDialogProps {
    legalEntityId: string;
    // The id of either the data processor or the sub processor type: no other type is granted by use.
    legalEntityTypeId: string;
    onDialogClose: () => void;
}

function LegalEntityUsageDialog(props: LegalEntityUsageDialogProps) {
    const { translateString } = useTranslation();
    const { getProcessingActivityOverviewUrl, getSystemOverviewUrl } = useUrlProvider();
    const { isLoading, data } = useLegalEntityTypeUsageDialog(props);
    const { getStandardType } = useLegalEntityTypeNames();
    const isDataProcessor = getStandardType(props.legalEntityTypeId) === StandardLegalEntityType.DataProcessor;
    const typeTranslationKey = isDataProcessor ? "ProcessorW" : "Subprocessor";

    return (
        <DotLegalDialog
            buttonOkText={translateString("close")}
            open
            onDialogClose={props.onDialogClose}
            onOkClick={props.onDialogClose}
            size={"sm"}
            header={translateString(typeTranslationKey)}
        >
            {isLoading ? (
                <DotLegalFullScreenSpinner />
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <DotLegalHeader headerStyle={"extraSmall"} fontWeight={400} center>
                        {translateString("legalEntityUsageDialogDescription", { type: translateString(typeTranslationKey).toLowerCase() })}
                    </DotLegalHeader>

                    {data && data.processingActivities.length > 0 && (
                        <>
                            <DotLegalHeader headerStyle={"medium"} marginBottom={1}>
                                {translateString("processingActivities")}
                            </DotLegalHeader>
                            {data.processingActivities.map((processingActivity) => (
                                <DotLegalHeader key={processingActivity.id} marginBottom={1} headerStyle={"extraSmall"} fontWeight={400}>
                                    <DotLegalLink to={getProcessingActivityOverviewUrl(processingActivity.id)} linkColor={"primary"}>
                                        {processingActivity.name}
                                    </DotLegalLink>
                                </DotLegalHeader>
                            ))}
                        </>
                    )}
                    {data && data.noOfProcessingActivitiesWithNoAccess > 0 && (
                        <DotLegalHeader headerStyle={"extraSmall"} fontWeight={400} marginBottom={0}>
                            {translateString("noOfProcessingActivitiesWithoutAccess", { count: data?.noOfProcessingActivitiesWithNoAccess })}
                        </DotLegalHeader>
                    )}

                    {data && data.systems.length > 0 && (
                        <>
                            <DotLegalHeader marginTop={data && data.noOfProcessingActivitiesWithNoAccess ? 2 : 1} marginBottom={1} headerStyle={"medium"}>
                                {translateString("systems")}
                            </DotLegalHeader>
                            {data.systems.map((system) => (
                                <DotLegalHeader key={system.id} marginBottom={1} headerStyle={"extraSmall"} fontWeight={400}>
                                    <DotLegalLink key={system.id} to={getSystemOverviewUrl(system.id)} linkColor={"primary"}>
                                        {system.name}
                                    </DotLegalLink>
                                </DotLegalHeader>
                            ))}
                        </>
                    )}
                    {data && data.noOfSystemsWithNoAccess > 0 && (
                        <DotLegalHeader headerStyle={"extraSmall"} fontWeight={400} marginBottom={0}>
                            {translateString("noOfSystemsWithoutAccess", { count: data?.noOfSystemsWithNoAccess })}
                        </DotLegalHeader>
                    )}
                </Box>
            )}
        </DotLegalDialog>
    );
}

export default LegalEntityUsageDialog;
